// Account-bound client settings: the sync core every settings store plugs into.
//
// Server truth lives in the `user_settings` collection (GET/PUT /api/users/me/settings,
// partial by key / full replace within a key). This module owns, per user:
//   - a localStorage warm cache (`healo-user-settings-cache:{uid}`) holding the last known
//     server values plus the keys with local changes not yet uploaded (`dirtyKeys`), so a
//     profile switch-back paints instantly and offline edits survive a quit;
//   - the login-time preload (bounded fetch BEFORE setAuthSession, so read-once consumers
//     see server values at first mount) with a dirty-preference merge — locally-dirty keys
//     beat the server copy and re-upload shortly after login;
//   - the debounced/dirty-retry PUT pipeline. Flush jobs stash {uid, token} at schedule
//     time: a profile switch flushes the OUTGOING user's changes with their still-valid
//     JWT, and a stale 401 is ignored by App's requestToken guard instead of tearing down
//     the fresh session.
//
// Stores (user-preference-store, saved-calendar-filters-store, pinned-messages-store,
// theme-store, dashboard-posts-store) pull their key lazily via readUserSetting — the lazy
// uid check makes reads correct even when they race the auth listener order — and push
// serialized values via writeUserSetting. subscribeUserSettingsActivation announces blob
// installs/swaps (login preload applied, profile switch, logout) so stores re-emit their
// own change events.
//
// Deliberately NO seeding from the pre-account localStorage blobs: first login after the
// migration starts from factory defaults (explicit product decision), and the legacy
// device-global keys are deleted on the first logged-in activation.

import { fetchMySettings, putMySettings } from "@/lib/api/user-settings";
import { getAuthToken, getAuthUser, subscribeAuthStateChange } from "@/lib/api/token-store";

const CACHE_KEY_PREFIX = "healo-user-settings-cache";
const ACTIVATION_EVENT = "healo-user-settings-activation";
const FLUSH_DEBOUNCE_MS = 800;
const RETRY_INTERVAL_MS = 30_000;

// Pre-migration device-global blobs. Never read (no seeding); removed once so stale
// shared-by-every-profile data can't mislead future debugging.
const LEGACY_DEVICE_GLOBAL_KEYS = [
  "healo-user-preferences",
  "healo-saved-calendar-filters",
  // Two migrations old (pre-dated even the unified preferences blob).
  "healo.messages.threadListFilters",
];

// The server-side allow-list, mirrored (app/user_settings_interactions.go). Values are
// always JSON objects on the wire: arrays wrapped {items:[...]}, scalars {value:...}.
export const USER_SETTING_KEYS = Object.freeze({
  UI_PREFERENCES: "uiPreferences",
  SAVED_CALENDAR_FILTERS: "savedCalendarFilters",
  PINNED_MESSAGES: "pinnedMessages",
  THEME: "theme",
  DASHBOARD_SEEN_AT: "dashboardSeenAt",
});

let activeUid = "";
let entry = null; // { settings: {key: value}, dirtyKeys: Set } for activeUid; null when logged out
// Separate from activeUid: a lazy read may swap the entry before the auth listener runs,
// and the activation event must still fire exactly once per identity change.
let lastActivationUid = null;
let authSubscribed = false;
let pendingJob = null; // { uid, token } stashed at schedule time — never the live token at flush time
let debounceTimer = null;
let retryTimer = null;
let flushInFlight = false;
let legacyKeysCleaned = false;
// uid:key → monotonically increasing write counter. A flush clears a key's dirty flag only
// when its generation is unchanged, so keys re-written mid-flight stay dirty.
const dirtyGeneration = new Map();

function canUseLocalStorage() {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function cacheKey(uid) {
  return `${CACHE_KEY_PREFIX}:${uid}`;
}

function generationKey(uid, key) {
  return `${uid}:${key}`;
}

function emitActivation() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(ACTIVATION_EVENT));
}

function readCacheEntry(uid) {
  const empty = { settings: {}, dirtyKeys: new Set() };
  if (!uid || !canUseLocalStorage()) return empty;
  try {
    const raw = window.localStorage.getItem(cacheKey(uid));
    if (!raw) return empty;
    const parsed = JSON.parse(raw);
    const settings =
      parsed?.settings && typeof parsed.settings === "object" ? parsed.settings : {};
    const dirtyKeys = new Set(
      (Array.isArray(parsed?.dirtyKeys) ? parsed.dirtyKeys : [])
        .map((key) => String(key || "").trim())
        .filter(Boolean),
    );
    return { settings, dirtyKeys };
  } catch {
    return empty;
  }
}

function persistCacheEntry(uid, cacheEntry) {
  if (!uid || !canUseLocalStorage()) return;
  try {
    window.localStorage.setItem(
      cacheKey(uid),
      JSON.stringify({ settings: cacheEntry.settings, dirtyKeys: [...cacheEntry.dirtyKeys] }),
    );
  } catch {
    /* ignore quota / serialization errors */
  }
}

function cleanupLegacyDeviceGlobalKeys() {
  if (legacyKeysCleaned || !canUseLocalStorage()) return;
  legacyKeysCleaned = true;
  for (const key of LEGACY_DEVICE_GLOBAL_KEYS) {
    try {
      window.localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  }
}

/**
 * Dirty-preference merge for the login preload: server values win for clean keys, the
 * local cache wins for keys still awaiting upload (they stay dirty and re-upload after
 * login). Pure — exported for tests.
 */
export function mergeServerSettingsPreferringDirty(serverSettings, cachedEntry) {
  const merged = {};
  const server = serverSettings && typeof serverSettings === "object" ? serverSettings : {};
  for (const [key, value] of Object.entries(server)) {
    if (value !== undefined) merged[key] = value;
  }
  const cachedSettings =
    cachedEntry?.settings && typeof cachedEntry.settings === "object" ? cachedEntry.settings : {};
  const dirtyKeys =
    cachedEntry?.dirtyKeys instanceof Set
      ? cachedEntry.dirtyKeys
      : new Set(Array.isArray(cachedEntry?.dirtyKeys) ? cachedEntry.dirtyKeys : []);
  for (const key of dirtyKeys) {
    if (cachedSettings[key] !== undefined) merged[key] = cachedSettings[key];
  }
  return merged;
}

function scheduleDebouncedFlush() {
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    debounceTimer = null;
    void flushPending();
  }, FLUSH_DEBOUNCE_MS);
  ensureRetryTimer();
}

function ensureRetryTimer() {
  if (retryTimer) return;
  retryTimer = setInterval(() => {
    void flushPending();
  }, RETRY_INTERVAL_MS);
}

function stopRetryTimer() {
  if (!retryTimer) return;
  clearInterval(retryTimer);
  retryTimer = null;
}

async function flushPending() {
  if (flushInFlight) return;
  const job = pendingJob;
  if (!job || !job.uid || !job.token) {
    stopRetryTimer();
    return;
  }
  const jobEntry = job.uid === activeUid && entry ? entry : readCacheEntry(job.uid);
  const settings = {};
  const flushedGenerations = new Map();
  for (const key of jobEntry.dirtyKeys) {
    if (jobEntry.settings[key] === undefined) continue;
    settings[key] = jobEntry.settings[key];
    flushedGenerations.set(key, dirtyGeneration.get(generationKey(job.uid, key)) || 0);
  }
  if (flushedGenerations.size === 0) {
    if (pendingJob === job) {
      pendingJob = null;
      stopRetryTimer();
    }
    return;
  }
  flushInFlight = true;
  try {
    await putMySettings({ settings, token: job.token });
    // Success clears exactly the flushed keys whose generation is unchanged; values are
    // never touched here, so a mid-flight switch-back plus fresh writes stay intact.
    const target = job.uid === activeUid && entry ? entry : readCacheEntry(job.uid);
    for (const [key, generation] of flushedGenerations) {
      if ((dirtyGeneration.get(generationKey(job.uid, key)) || 0) === generation) {
        target.dirtyKeys.delete(key);
      }
    }
    persistCacheEntry(job.uid, target);
    if (target.dirtyKeys.size === 0 && pendingJob === job) {
      pendingJob = null;
      stopRetryTimer();
    }
  } catch (error) {
    // Keep the dirty flags; the 30s tick, the next write, or the next login's
    // dirty-preference merge re-uploads. A stale 401 here is harmless (requestToken guard).
    console.warn("[UserSettings] flush failed; changes stay queued", error?.status || error);
    ensureRetryTimer();
  } finally {
    flushInFlight = false;
  }
}

function handleAuthChange() {
  const uid = String(getAuthUser()?.id || "").trim();
  if (uid !== activeUid) {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
    // Flush the outgoing user's edits immediately with their stashed token (still valid
    // for hours after a profile switch).
    if (pendingJob && pendingJob.uid && pendingJob.uid !== uid) {
      void flushPending();
    }
    activeUid = uid;
    entry = uid ? readCacheEntry(uid) : null;
  }
  if (uid !== lastActivationUid) {
    lastActivationUid = uid;
    if (uid) cleanupLegacyDeviceGlobalKeys();
    emitActivation();
    // A freshly-installed entry can carry dirty keys (offline quit, failed preload flush) —
    // re-upload them without waiting for the next write.
    if (uid && entry && entry.dirtyKeys.size > 0) {
      pendingJob = { uid, token: getAuthToken() };
      scheduleDebouncedFlush();
    }
  }
}

function ensureAuthSubscription() {
  if (authSubscribed || typeof window === "undefined") return;
  authSubscribed = true;
  subscribeAuthStateChange(handleAuthChange);
}

// Correctness fallback for reads racing the auth-listener order: swaps the entry to the
// current identity WITHOUT emitting activation (handleAuthChange still does that once).
function ensureActiveEntry() {
  ensureAuthSubscription();
  const uid = String(getAuthUser()?.id || "").trim();
  if (uid === activeUid && (entry || !uid)) return;
  activeUid = uid;
  entry = uid ? readCacheEntry(uid) : null;
}

/** The uid whose settings the read/write API currently serves ("" when logged out). */
export function getActiveSettingsUid() {
  ensureActiveEntry();
  return activeUid;
}

/** Raw stored value for an account settings key, or undefined (logged out / never saved). */
export function readUserSetting(key) {
  ensureActiveEntry();
  const normalizedKey = String(key || "").trim();
  if (!normalizedKey || !activeUid || !entry) return undefined;
  return entry.settings[normalizedKey];
}

/**
 * Write an account settings key: in-memory + warm cache synchronously, server via a
 * debounced PUT with dirty-retry. No-op when logged out (account settings have no owner).
 * Returns whether the write was accepted.
 */
export function writeUserSetting(key, value) {
  ensureActiveEntry();
  const normalizedKey = String(key || "").trim();
  if (!normalizedKey) return false;
  if (!activeUid || !entry) {
    if (import.meta.env?.DEV) {
      console.warn(`[UserSettings] dropped logged-out write for ${normalizedKey}`);
    }
    return false;
  }
  entry.settings = { ...entry.settings, [normalizedKey]: value };
  entry.dirtyKeys.add(normalizedKey);
  const genKey = generationKey(activeUid, normalizedKey);
  dirtyGeneration.set(genKey, (dirtyGeneration.get(genKey) || 0) + 1);
  persistCacheEntry(activeUid, entry);
  pendingJob = { uid: activeUid, token: getAuthToken() };
  scheduleDebouncedFlush();
  return true;
}

/** Fired once per identity swap after the entry is installed (login, switch, logout). */
export function subscribeUserSettingsActivation(listener) {
  if (typeof window === "undefined" || typeof listener !== "function") {
    return () => {};
  }
  ensureAuthSubscription();
  const handler = () => listener();
  window.addEventListener(ACTIVATION_EVENT, handler);
  return () => {
    window.removeEventListener(ACTIVATION_EVENT, handler);
  };
}

/**
 * Login-time preload: bounded server fetch merged into the warm cache BEFORE
 * setAuthSession fires, so every consumer's first read after login sees server values.
 * Resolves on every path — login must never block on, or fail because of, settings.
 */
export async function preloadUserSettingsForLogin({ uid, token, timeoutMs = 5000 } = {}) {
  ensureAuthSubscription();
  const normalizedUid = String(uid || "").trim();
  const normalizedToken = String(token || "").trim();
  if (!normalizedUid || !normalizedToken || typeof window === "undefined") return;
  let serverSettings = null;
  const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timeout = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
  try {
    serverSettings = await fetchMySettings({ token: normalizedToken, signal: controller?.signal });
  } catch (error) {
    // Warm cache (else defaults) carries the session; the next login reconciles.
    console.warn(
      "[UserSettings] login preload failed; using cached/default settings",
      error?.status || error,
    );
  } finally {
    if (timeout) clearTimeout(timeout);
  }
  if (!serverSettings || typeof serverSettings !== "object") return;
  const cached = readCacheEntry(normalizedUid);
  const nextEntry = {
    settings: mergeServerSettingsPreferringDirty(serverSettings, cached),
    dirtyKeys: cached.dirtyKeys,
  };
  persistCacheEntry(normalizedUid, nextEntry);
  if (normalizedUid === activeUid) {
    entry = nextEntry;
  }
}

/** Purge a user's warm cache (remove-from-device; see profile-roster-store's doom loop). */
export function purgeUserSettingsCache(uid) {
  const normalizedUid = String(uid || "").trim();
  if (!normalizedUid || !canUseLocalStorage()) return;
  try {
    window.localStorage.removeItem(cacheKey(normalizedUid));
  } catch {
    /* ignore */
  }
}
